import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { BsEye, BsEyeSlash } from 'react-icons/bs'
import { FiLock } from 'react-icons/fi'
import Swal from 'sweetalert2'
import Loader from '../Loader'
import AuthLayout from '../authLayout/AuthLayout'

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3500,
  timerProgressBar: true,
})

// Opened from the emailed link: /resetpassword/:token
const PasswordReset = ({ route }) => {
  const { token } = useParams()
  const navigate = useNavigate()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loader, setLoader] = useState(false)
  const [linkInvalid, setLinkInvalid] = useState(false)

  const resetPassword = async () => {
    if (newPassword.length < 6) return Toast.fire({ icon: 'warning', title: 'Password must be at least 6 characters' })
    if (newPassword !== confirmPassword) return Toast.fire({ icon: 'warning', title: "Passwords don't match" })

    setLoader(true)
    try {
      const res = await (await fetch(`${route}/api/resetpassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      })).json()
      if (res.status === 'ok') {
        Toast.fire({ icon: 'success', title: 'Password reset. You can sign in now.' })
        navigate('/login')
      } else if (res.status === 400 && /link/i.test(res.message || '')) {
        setLinkInvalid(true)
      } else {
        Toast.fire({ icon: 'error', title: res.message || 'Something went wrong. Please try again.' })
      }
    } catch {
      Toast.fire({ icon: 'error', title: 'Something went wrong. Please try again.' })
    } finally {
      setLoader(false)
    }
  }

  if (linkInvalid) {
    return (
      <AuthLayout heading="Link expired" subheading="This reset link is invalid, has expired, or was already used.">
        <div className="auth-form">
          <Link to="/passwordreset" className="auth-submit auth-submit-link">Request a new link</Link>
          <p className="auth-switch"><Link to="/login" className="auth-link">Back to sign in</Link></p>
        </div>
      </AuthLayout>
    )
  }

  const passwordField = (id, label, value, setValue, shown, setShown, autoComplete) => (
    <div className="auth-field">
      <label htmlFor={id}>{label}</label>
      <div className="auth-input-wrap">
        <span className="auth-input-icon"><FiLock size={17} /></span>
        <input
          id={id}
          className="auth-input has-toggle"
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          placeholder="At least 6 characters"
          required
          minLength={6}
          value={value}
          onChange={(e) => setValue(e.target.value.trim())}
        />
        <button type="button" className="auth-eye" onClick={() => setShown(!shown)} aria-label={shown ? 'Hide password' : 'Show password'}>
          {shown ? <BsEye size={17} /> : <BsEyeSlash size={17} />}
        </button>
      </div>
    </div>
  )

  return (
    <AuthLayout heading="Choose a new password" subheading="Pick a password you haven't used here before.">
      {loader && <Loader />}
      <form className="auth-form" onSubmit={(e) => { e.preventDefault(); resetPassword() }}>
        {passwordField('password_field', 'New password', newPassword, setNewPassword, showPassword, setShowPassword, 'new-password')}
        {passwordField('confirm_password_field', 'Confirm new password', confirmPassword, setConfirmPassword, showConfirmPassword, setShowConfirmPassword, 'new-password')}
        <button type="submit" className="auth-submit">Reset password</button>
        <p className="auth-switch"><Link to="/login" className="auth-link">Back to sign in</Link></p>
      </form>
    </AuthLayout>
  )
}

export default PasswordReset
